import { memo } from "react";
import { StatusDot, type StatusTone } from "@/components/ui";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { CardPreview } from "@/components/shapes/shared/card-preview";
import {
  frameAttentionText,
  useFrameAttention,
  type FrameAttention,
} from "@/components/shapes/shared/use-frame-attention";
import {
  AGENT_TARGET_RING_CLASS,
  agentTargetRingStyle,
  useFrameTargetingColor,
} from "@/components/shapes/shared/use-frame-targeting-color";
import { translate, useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import { wallMessages } from "@/i18n/messages/wall";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { useSelectionStore } from "@/stores/selection-store";
import { WallCardActions } from "./wall-card-actions";
import { useWallFrame } from "./wall-frame-source";
import { wallCardPressHandlers } from "./wall-selection";

/** The faint outline a card or Pinned tile shows under the pointer, no lift (mymind study). */
export const WALL_HOVER_OUTLINE_CLASS =
  "outline outline-2 outline-transparent motion-safe:transition-[outline-color] motion-safe:duration-100 group-hover/wall-item:outline-[color:var(--card-edge-raised)]";

/**
 * A wall card's surface (mockup `.card`, Proposed): its paint comes from
 * the card; the radius and soft shadow with no edge; under the pointer a
 * faint outline, no lift (mymind study); the ring of its focused button.
 */
export const WALL_CARD_SURFACE_CLASS = cn(
  "relative overflow-hidden rounded-card shadow-card-wall",
  WALL_HOVER_OUTLINE_CLASS,
  "has-[>button:focus-visible]:ring-2 has-[>button:focus-visible]:ring-accent",
);

export interface WallCardProps {
  id: string;
  /** Revealed for the workspace, or new work arrived: one ring pulse. */
  flashing?: boolean;
  /** New work at the top of the wall: it rises into place once. */
  rising?: boolean;
  /**
   * Open over the wall, or returning into its slot: the opened item is the
   * card, so its slot stays empty (hidden, still laid out).
   */
  opened?: boolean;
  /** A plain click; a ⌘/Ctrl- or Shift-click selects the card instead. */
  onOpen: (id: string) => void;
  onMenu: (id: string, anchor: ContextMenuAnchor) => void;
}

interface CardStatus {
  tone: StatusTone;
  text: string;
}

/** The working agent's colour ring over a card or a Pinned tile. */
export function WallAgentTargetRing({ id }: { id: string }) {
  const color = useFrameTargetingColor(id);
  if (!color) return null;
  return (
    <div
      className={cn(
        AGENT_TARGET_RING_CLASS,
        "pointer-events-none absolute inset-0 z-[3] rounded-card",
      )}
      style={agentTargetRingStyle(color)}
      data-testid={`wall-card-agent-target-${id}`}
      aria-hidden="true"
    />
  );
}

/** A question waits first, then an error, then work in progress. */
function cardStatus(
  frame: FrameData,
  attention: FrameAttention,
): CardStatus | null {
  if (attention) {
    return { tone: attention.kind, text: frameAttentionText(attention) ?? "" };
  }
  if (frame.status === "streaming") {
    return { tone: "working", text: translate(uiMessages, "statusWorking") };
  }
  return null;
}

function WallCardBody({
  id,
  flashing = false,
  rising = false,
  opened = false,
  onOpen,
  onMenu,
}: WallCardProps) {
  const t = useT(wallMessages);
  const frame = useWallFrame(id);
  const attention = useFrameAttention(id);
  const selected = useSelectionStore((state) =>
    state.multiSelectedFrameIds.includes(id),
  );
  if (!frame) return null;

  const title = frameDisplayTitle(frame);
  const status = cardStatus(frame, attention);

  return (
    <div
      className={cn(
        "group/wall-item relative",
        rising && "motion-safe:animate-wall-rise",
        opened && "invisible",
      )}
      // The card takes the pointer on the desktop, and a wheel over it
      // scrolls the wall.
      data-interactive="true"
      data-wall-card=""
    >
      <div
        className={cn(
          WALL_CARD_SURFACE_CLASS,
          "bg-card-surface text-card-ink",
          selected && "ring-2 ring-accent",
          flashing && "motion-safe:animate-wall-flash",
        )}
        data-testid="wall-card-surface"
      >
        <button
          type="button"
          className="block w-full cursor-pointer select-none text-left outline-none"
          aria-label={[title, status?.text, selected && t("selected")]
            .filter(Boolean)
            .join(", ")}
          title={title}
          data-testid={`wall-card-${id}`}
          data-selected={selected || undefined}
          data-frame-type={frame.frameType}
          data-status={status?.tone}
          {...wallCardPressHandlers(id, onOpen, onMenu)}
        >
          <CardPreview frame={frame} density="regular" />
        </button>
        <WallAgentTargetRing id={id} />
        {status && (
          <StatusDot
            tone={status.tone}
            className={cn(
              "pointer-events-none absolute right-4 top-4 z-[2] shadow-[0_0_0_2.5px_var(--card-surface)] transition-opacity group-hover/wall-item:opacity-0",
              // The soft working dot reads on paper and on ink.
              status.tone === "working" && "bg-card-body",
            )}
          />
        )}
        <WallCardActions
          id={id}
          title={title}
          selected={selected}
          onOpen={onOpen}
          onMenu={onMenu}
        />
      </div>
    </div>
  );
}

/** A wall card: re-renders only for its own frame (and its own props). */
export const WallCard = memo(WallCardBody);
